import { Link } from "react-router-dom";
import { formatViews } from "@/utils/format";

export type ArticleLike = {
  id: string;
  title: string;
  excerpt: string;
  categoryId: string;
  categoryName: string;
  author: string;
  date: string;
  readMinutes: number;
  views: number;
  heat: number;
  tags: string[];
  cover: string;
};

type ArticleCardProps = {
  article: ArticleLike;
  variant?: "default" | "compact" | "feature";
};

export default function ArticleCard({
  article,
  variant = "default",
}: ArticleCardProps) {
  if (variant === "compact") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 items-start py-4 border-b border-background-200/80 last:border-b-0 cursor-pointer"
      >
        <div className="w-24 h-20 md:w-28 md:h-24 shrink-0 rounded-lg overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - ${article.categoryName}`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0 flex-1">
          <span className="text-xs font-label text-primary-600">
            {article.categoryName}
          </span>
          <h3 className="mt-1 font-heading font-bold text-sm md:text-base text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            {article.title}
          </h3>
          <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500 font-label">
            <span className="inline-flex items-center gap-1">
              <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
              {formatViews(article.views)}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-time-line w-3.5 h-3.5 flex items-center justify-center"></i>
              {article.readMinutes} 分钟
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === "feature") {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group relative block rounded-lg overflow-hidden bg-foreground-950 cursor-pointer"
      >
        <div className="relative w-full h-[300px] md:h-[420px]">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - ${article.categoryName}`}
            className="w-full h-full object-cover object-top opacity-80 transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/50 to-transparent"></div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-8">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-label">
            <i className="ri-fire-fill w-3.5 h-3.5 flex items-center justify-center"></i>
            热度 {article.heat}
          </span>
          <h3 className="mt-3 font-heading text-xl md:text-2xl font-bold text-background-50 leading-snug line-clamp-2">
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-background-200 line-clamp-2 hidden sm:block">
            {article.excerpt}
          </p>
          <div className="mt-4 flex items-center gap-4 text-xs text-background-300 font-label">
            <span>{article.author}</span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
              {formatViews(article.views)}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <article className="group h-full flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200/80 hover:border-primary-200 transition-colors">
      <Link
        to={`/article/${article.id}`}
        className="block w-full h-44 overflow-hidden bg-background-200 cursor-pointer"
      >
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - ${article.categoryName}`}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-col flex-1 p-4 md:p-5">
        <div className="flex items-center gap-2">
          <Link
            to={`/topic/${article.categoryId}`}
            className="text-xs font-label px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors cursor-pointer"
          >
            {article.categoryName}
          </Link>
          <span className="text-xs text-foreground-400 font-label">
            热度 {article.heat}
          </span>
        </div>
        <h3 className="mt-3 font-heading font-bold text-base md:text-lg text-foreground-900 leading-snug line-clamp-2">
          <Link
            to={`/article/${article.id}`}
            className="hover:text-primary-600 transition-colors cursor-pointer"
          >
            {article.title}
          </Link>
        </h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2 flex-1">
          {article.excerpt}
        </p>
        <div className="mt-4 pt-3 border-t border-background-200/80 flex items-center justify-between text-xs text-foreground-500 font-label">
          <span>{article.author}</span>
          <span className="inline-flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
              {formatViews(article.views)}
            </span>
            <span className="inline-flex items-center gap-1">
              <i className="ri-time-line w-3.5 h-3.5 flex items-center justify-center"></i>
              {article.readMinutes} 分钟
            </span>
          </span>
        </div>
      </div>
    </article>
  );
}